<template>
    <BasePage>
        <div class="container">
            <div class="container">
                <div v-if="loaded && notReadyMessage" class="card mt-6 py-6 px-3 text-center" role="status" data-testid="balance-not-ready">
                    <h2>Your balance isn't ready yet</h2>
                    <p class="mb-0">{{ notReadyMessage }}</p>
                </div>
                <div v-if="loaded && !notFound && !notReadyMessage" class="card mt-6">
                    <div class="card-header">
                        <div class="card-title">
                            <div>Order N<sup>o</sup> {{ order.id }} | Outstanding Balance</div>
                        </div>
                    </div>
                    <div class="card-body" v-if="order.balanceStatus !== 'paid'">
                        Deposit paid: {{ formatGBP(order.depositAmount) }}
                    </div>
                    <div class="card-body" v-if="order.balanceStatus !== 'paid'">
                        <table class="table card-table table-bordered">
                            <thead class="table-striped-columns">
                            <th>Item</th>
                            <th>Size/Qty</th>
                            <th>Actual</th>
                            <th>Price</th>
                            </thead>
                            <tbody>
                            <tr v-bind:key="i.id" v-for="i in order.items">
                                <td>{{ i.item }}</td>
                                <!-- L-82 (WP-40): the option as chosen ("Large Three (about 4 kg)"), then the
                                     actual weight it is priced at ("4.12 kg"), or "× 2" for an each item. -->
                                <td>{{ sizeText(i) }}</td>
                                <td v-if="i.unit === 'each'">{{ describeAmount('each', i.size) }}</td>
                                <td v-if="i.unit !== 'each'">{{ describeAmount(i.unit, i.allocatedWeight) }}</td>
                                <td v-if="i.unit !== 'each'">{{ formatGBP(i.allocatedWeight * i.price) }}</td>
                                <td v-if="i.unit === 'each'">{{ formatGBP(i.size * i.price) }}</td>
                            </tr>
                            <tr v-if="discountSaving > 0" style="padding-top: 10px" class="border-0" data-testid="balance-discount">
                                <td class="border-0"></td>
                                <td class="border-0"></td>
                                <td class="border-0 border-bottom text-end">Quantity discount: </td>
                                <td class="border-0 border-bottom">- {{formatGBP(discountSaving)}}</td>
                            </tr>
                            <tr style="padding-top: 10px" class="border-0">
                                <td class="border-0"></td>
                                <td class="border-0"></td>
                                <td class="border-0 border-bottom text-end">Subtotal: </td>
                                <td class="border-0 border-bottom">{{formatGBP(order.total)}}</td>
                            </tr>
                            <tr style="padding-top: 10px" class="border-0">
                                <td class="border-0"></td>
                                <td class="border-0"></td>
                                <td class="border-0 border-bottom text-end">Deposit: </td>
                                <td class="border-0 border-bottom">- {{formatGBP(order.depositAmount)}}</td>
                            </tr>
                            <tr style="padding-top: 10px" class="border-0">
                                <td class="border-0"></td>
                                <td class="border-0"></td>
                                <td class="border-0 border-bottom text-end">Remaining: </td>
                                <td class="border-0 border-bottom">{{formatGBP(order.total - order.depositAmount)}}</td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                    <div  v-if="order.balanceStatus === 'paid'" class="card-body">
                        You have already paid your remaining Christmas Balance.
                    </div>
                    <div class="card-footer"  v-if="order.balanceStatus !== 'paid'">
                        <div class="card-actions d-flex justify-content-end">
                            <button type="button" :disabled="paying" @click="gotToPayment()" class="btn btn-primary">Pay {{formatGBP(order.total - order.depositAmount)}} Now</button>
                        </div>
                    </div>
                </div>
                <div v-if="payError" class="alert alert-danger mt-3" role="alert">{{ payError }}</div>
                <div v-if="loaded && notFound" class="card mt-6 py-6 px-3 text-center" role="alert">
                    <h2>We couldn't find this order</h2>
                    <p class="mb-0">The payment link may be incomplete or out of date. Please check the link in your
                        email, or email us at <a href="mailto:info@larkhallbutchers.co.uk">info@larkhallbutchers.co.uk</a>.</p>
                </div>
            </div>
        </div>
    </BasePage>
</template>

<script>
import BasePage from "@/components/BasePage.vue";
import api from "@/services/api";
import { describeAmount, describeSize } from "@/services/sizeText";

export default {
    name: 'BillingPage',
    components: {
        BasePage,
    },
    data() {
        return {
            order: {},
            loaded: false,
            notFound: false,
            notReadyMessage: '',
            paying: false,
            payError: ''
        }
    },
    computed: {
        // The lines above are listed at full price; the API's total already has any quantity
        // discount taken off (register L-73). Show the difference so the lines add up.
        discountSaving() {
            const items = Array.isArray(this.order.items) ? this.order.items : [];
            const fullPrice = items.reduce((sum, i) => {
                const qty = i.unit === 'each' ? i.size : i.allocatedWeight;
                return sum + Number(qty) * Number(i.price);
            }, 0);
            const saving = Math.round(fullPrice - Number(this.order.total));
            return Number.isFinite(saving) && saving > 0 ? saving : 0;
        }
    },
    methods: {
        describeAmount,
        sizeText(i) {
            return i.sizeText || describeSize(i.unit, i.size, i.sizeLabel);
        },
        formatGBP(pence) {
            const pounds = pence / 100;
            return new Intl.NumberFormat('en-GB', {
                style: 'currency',
                currency: 'GBP'
            }).format(pounds);
        },
        async gotToPayment() {
            this.paying = true;
            this.payError = '';
            try {
                const res = await api.post(`/api/billing/create-session/${this.$route.params.guid}`);
                if (!res.data || !res.data.url) throw new Error('No payment link returned');
                window.location = res.data.url;
            } catch (e) {
                this.paying = false;
                this.payError = "We couldn't start your payment. Please try again.";
            }
        }
    },
    async mounted() {
        try {
            const res = await api.get(`/api/billing/${this.$route.params.guid}`);
            this.order = res.data || {};
        } catch (e) {
            this.order = {};
        }
        const o = this.order;
        // A real order that is not priced yet answers 200 with only `error` text
        // (getOrderByGUID in worker/src/routes/billing.ts): show that text, with no pay button.
        if (typeof o.error === 'string' && o.error.trim() !== '') {
            this.notReadyMessage = o.error;
            this.loaded = true;
            return;
        }
        // A usable order has numeric amounts; a failed request or an empty reply is "not found".
        this.notFound = o.total == null || o.depositAmount == null
            || !Number.isFinite(Number(o.total)) || !Number.isFinite(Number(o.depositAmount));
        this.loaded = true;
    }
}
</script>